import React, {
  useCallback,
  useMemo,
  useState,
  useRef,
  PropsWithChildren,
  Children,
  useEffect,
} from "react";
import { FormattedMessage, useIntl } from "react-intl";
import styled from "styled-components";
import { useAppState } from "../../AppContext";
import { Capability, useCapability } from "../../hooks/useCapability";
import { useCopy } from "../../hooks/useCopy";
import { useUpdateStateUrl } from "../../hooks/useUpdateStateUrl";
import { Modal, ModalProps } from "../../shared/components/Modal";

export const ShareModal = (props: ModalProps) => {
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedHtml, setCopiedHtml] = useState(false);
  const [linkTimeout, setLinkTimeout] = useState<number | null>(null);
  const [htmlTimeout, setHtmlTimeout] = useState<number | null>(null);
  const { state } = useAppState();
  const canReadDraft = useCapability(Capability.ReadDraft);
  const updateStateUrl = useUpdateStateUrl();
  const intl = useIntl();

  const linkRef = useRef<HTMLInputElement>(null);
  const copyLink = useCopy(linkRef);
  const embedRef = useRef<HTMLTextAreaElement>(null);
  const copyEmbed = useCopy(embedRef);

  const [flags, setFlags] = useState(new Set<string>());

  const toggleFlag = useCallback((flag: string, value: boolean) => {
    setFlags((prev) => {
      const newFlags = new Set(prev);

      if (value) {
        newFlags.add(flag);
      } else {
        newFlags.delete(flag);
      }

      return newFlags;
    });
  }, []);

  const allowEmbedding = useMemo(() => {
    if (!state.enableEmbed) return false;

    if (
      !state.enableEmbedAncestors ||
      state.enableEmbedAncestors.trim().match(/^https?:(\/\/\*)?$/)
    ) {
      return true;
    }

    return canReadDraft;
  }, [canReadDraft, state.enableEmbed, state.enableEmbedAncestors]);

  const searchParameters = useMemo(() => {
    const params = new URLSearchParams();

    flags.forEach((flag) => params.set(flag, ""));

    return params.toString().replace(/=(&|$)/g, "$1");
  }, [flags]);

  const iframeString = useMemo(() => {
    const stateUrl = updateStateUrl({ embed: true }, window.location.pathname, true);
    const search = searchParameters.length ? `?${searchParameters}` : "";
    const frameSrc = `${window.location.origin}${stateUrl}${search}`;

    return `<iframe src="${frameSrc}" width="1000px" height="700px" seamless allowfullscreen />`;
  }, [updateStateUrl, searchParameters]);

  const copyLinkString = useMemo(() => {
    const search = searchParameters.length ? `?${searchParameters}` : "";
    return `${window.location.origin}${window.location.pathname}${search}`;
  }, [searchParameters]);

  const copyShareLink = useCallback(async () => {
    await copyLink();

    setCopiedLink(true);
    if (linkTimeout) clearTimeout(linkTimeout);
    setLinkTimeout(
      window.setTimeout(() => {
        setCopiedLink(false);
      }, 3000)
    );
  }, [linkTimeout, setCopiedLink, setLinkTimeout, copyLink]);

  const copyEmbedHtml = useCallback(async () => {
    await copyEmbed();

    setCopiedHtml(true);
    if (htmlTimeout) clearTimeout(htmlTimeout);
    setHtmlTimeout(
      window.setTimeout(() => {
        setCopiedHtml(false);
      }, 3000)
    );
  }, [htmlTimeout, setCopiedHtml, setHtmlTimeout, copyEmbed]);

  const copyLabel = intl.formatMessage({
    id: "share-model.copy-label",
    defaultMessage: "Copy",
    description:
      "Label for a 'copy' button in the share modal when the associated field has not been copied.",
  });
  const copiedLabel = intl.formatMessage({
    id: "share-model.copied-label",
    defaultMessage: "Copied",
    description:
      "Label for a 'copy' button in the share modal after the associated field has been copied.",
  });

  return (
    <Modal {...props}>
      <FormattedMessage
        tagName="h2"
        id="share-modal.title"
        defaultMessage="Share this platform"
        description="Title for the share modal."
      />
      <p>
        <FormattedMessage
          id="share-modal.link-intro"
          defaultMessage="Would you like acquaintaces to view this platform, share the link to the full platform."
          description="Introductory text for sharing the platform link."
        />
        {allowEmbedding && (
          <>
            <br />
            <FormattedMessage
              id="share-modal.embed-intro"
              defaultMessage="Or choose to embed this platform into an online page, like a news post."
              description="Introductory text for embedding the platform."
            />
          </>
        )}
      </p>
      <FormattedMessage
        tagName="h3"
        id="share-modal.share-link-header"
        defaultMessage="Share a link"
        description="Call to action header displayed directly above the share-link."
      />
      <CopyWrapper
        onClick={copyShareLink}
        $afterContent={copiedLink ? copiedLabel : copyLabel}
        $success={copiedLink}
      >
        <input
          type="text"
          readOnly
          ref={linkRef}
          value={copyLinkString}
          id="share-link"
          onClick={(e) => e.stopPropagation()}
        />
      </CopyWrapper>
      {allowEmbedding && (
        <>
          <FormattedMessage
            tagName="h3"
            id="share-modal.embed-link-header"
            defaultMessage="Embed the platform"
            description="Call to action header displayed directly above the embed-link."
          />
          <CopyWrapper
            onClick={copyEmbedHtml}
            $afterContent={copiedHtml ? copiedLabel : copyLabel}
            $success={copiedHtml}
          >
            <textarea
              readOnly
              ref={embedRef}
              value={iframeString}
              rows={6}
              id="share-embed"
              onClick={(e) => e.stopPropagation()}
            />
          </CopyWrapper>
        </>
      )}
      <FormattedMessage
        tagName="h3"
        id="share-modal.options.header"
        defaultMessage="Url parameters"
        description="Header for the share url options section of the share modal."
      />
      <FlagWrapper>
        {!window.location.pathname.includes("/map") && <Flag name="hide-map" onChange={toggleFlag}>
          <FormattedMessage
            id="share-modal.options.hide-map"
            defaultMessage="Hide the map"
            description="Label for a checkbox that toggles whether the map is shown in the shared or embedded view."
          />
        </Flag>}
        <Flag name="hide-menu" onChange={toggleFlag}>
          <FormattedMessage
            id="share-modal.options.hide-menu"
            defaultMessage="Hide the menu"
            description="Label for a checkbox that toggles whether the menu is shown in the shared or embedded view."
          />
        </Flag>
        <Flag name="hide-controls" onChange={toggleFlag}>
          <FormattedMessage
            id="share-modal.options.hide-controls"
            defaultMessage="Hide zoom controls"
            description="Label for a checkbox that toggles whether the zoom controls are shown in the shared or embedded view."
          />
        </Flag>
        <Flag name="hide-hotspots" onChange={toggleFlag}>
          <FormattedMessage
            id="share-modal.options.hide-hotspots"
            defaultMessage="Hide hotspot navigation"
            description="Label for a checkbox that toggles whether the hotspot navigation is shown in the shared or embedded view."
          />
        </Flag>
        <Flag name="hide-scenarios" onChange={toggleFlag}>
          <FormattedMessage
            id="share-modal.options.hide-scenarios"
            defaultMessage="Hide scenario switch"
            description="Label for a checkbox that toggles whether the scenario switch is shown in the shared or embedded view."
          />
        </Flag>
        <Flag name="hide-ask-question" onChange={toggleFlag}>
          <FormattedMessage
            id="share-modal.options.hide-ask-question"
            defaultMessage="Hide ask question"
            description="Label for a checkbox that toggles whether the ask question button is shown in the shared or embedded view."
          />
        </Flag>
        <p>
          <FormattedMessage
            tagName="b"
            id="share-modal.flags-info"
            defaultMessage="Note that when any hide-flag is active, this modal will not be available in the shared or embedded view."
            description="Info text shown below the share flags."
          />
        </p>
      </FlagWrapper>
    </Modal>
  );
};

interface CopyWrapperProps {
  $afterContent: string;
  $success?: boolean;
}

const CopyWrapper = styled.div<CopyWrapperProps>`
  width: auto;
  height: auto;
  position: relative;
  pointer-events: none;

  input[type="text"],
  textarea {
    padding-right: 90px;
    pointer-events: all;
  }

  &:after {
    display: block;
    position: absolute;
    content: "${(props) => props.$afterContent}";
    color: var(${(props) => (props.$success ? "--color-success-50" : "--color-primary-50")});
    right: var(--space-25);
    top: var(--space-30);
    pointer-events: all;
    text-align: right;
    cursor: pointer;
  }

  + h3 {
    margin-top: var(--space-25);
  }
`;

const FlagWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-20);
`;

interface FlagProps {
  name: string;
  onChange: (flag: string, value: boolean) => void;
}
const Flag = ({ name, onChange, children }: PropsWithChildren<FlagProps>) => {
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    onChange(name, checked);
  }, [checked]);

  return (
    <Label htmlFor={`flag-${name}`}>
      <input
        type="checkbox"
        onChange={() => setChecked((c) => !c)}
        id={`flag-${name}`}
        name={`flag-${name}`}
        checked={checked}
      />
      {children}
    </Label>
  );
};

const Label = styled.label`
  display: flex;
  width: fit-content;
  padding-right: var(--space-30);
  align-items: center;
  background-color: var(--color-neutral-10);
  border-radius: var(--border-radius-10);
  color: black;

  > input {
    margin: var(--space-20);
  }
`;
